import React from 'react';

import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';

import { RelatedArticles, useOpenArticle } from 'components/knz/embeds/blocks/ArticleNav';
import { ArticleSummary, getArticle, listArticle } from 'stores/ArticleStoreAdapter';
import { LabelResult } from 'stores/LabelStoreAdapter';

/** デフォルトの表示件数（これ以降は「もっと見る」で展開する）。 */
const DEFAULT_VISIBLE = 6;

/** 「もっと見る」で展開しても表示する最大件数（人気ラベルで数百件ヒットした際の安全弁）。 */
const MAX_TOTAL = 30;

/** おすすめとして表示する最小の関連度（共通ラベル数）。これ未満は除外する。 */
const MIN_RELEVANCE = 2;

/**
 * 当該記事のラベルを持つ他記事を集計し「おすすめ記事」として並べるためのソート結果。
 * - relevance: 当該記事との共通ラベル数（関連度）
 */
type Scored = {
  article: ArticleSummary;
  relevance: number;
};

/**
 * 当該記事のラベルを起点に、共通ラベル数（関連度）でおすすめ記事を並べる。
 * - 第1キー: 関連度（共通ラベル数）の降順
 * - 第2キー: 公開日時（createdAt）の新しい順
 * self（当該記事）と excludeIds（本文 ArticleNav に既出の記事・先頭固定の溢れ記事）は除外する。
 */
const scoreAndSort = (
  candidates: ArticleSummary[],
  selfLabelIds: Set<string>,
  selfArticleId: string,
  excludeIds: Set<string>,
): ArticleSummary[] => {
  const scored: Scored[] = [];
  candidates.forEach((article) => {
    if (article.articleId === selfArticleId) return;
    if (excludeIds.has(article.articleId)) return;
    const relevance = article.labels.reduce(
      (acc, l) => (selfLabelIds.has(l.labelId) ? acc + 1 : acc),
      0,
    );
    // 共通ラベルが 2 個以上の記事だけをおすすめとして表示する（関連度が低い記事を除外）。
    if (relevance < MIN_RELEVANCE) return;
    scored.push({ article, relevance });
  });

  scored.sort((a, b) => {
    if (b.relevance !== a.relevance) return b.relevance - a.relevance;
    // createdAt は ISO 風文字列（yyyy-MM-dd'T'HH:mm:ss）なので文字列比較で新しい順にできる。
    return b.article.createdAt.localeCompare(a.article.createdAt);
  });

  return scored.map((s) => s.article);
};

/**
 * 記事詳細下部の「おすすめ記事」欄。
 *
 * 構成（上から）:
 * 1. 溢れ記事（pinnedArticleIds）: 本文 ArticleNav の「関連する記事」が上限（6 件）を超えて
 *    表示しきれなかった分。記法（articles）の並び順のまま先頭に固定表示する（ラベル関連度は無視）。
 * 2. ラベル集計おすすめ: 当該記事のラベルを持つ記事を検索（OR）し、共通ラベル数を関連度として
 *    集計、関連度の高い順（同率は公開日時の新しい順）に並べる（共通ラベル 2 個以上のみ）。
 *
 * 見た目は ArticleNav の関連記事（RelatedArticles / RelatedCard）と同一。全体で初期 6 件表示し
 * 「もっと見る」で最大 MAX_TOTAL 件まで展開する（溢れ記事を優先して埋める）。
 *
 * - 溢れも無く・ラベル集計おすすめも無い場合は欄自体を描画しない（null）。
 * - self・excludeArticleIds（ArticleNav に既出）・pinned は互いに重複させない。
 * - 表示件数を親へ通知し（onCountChange）、親が末尾の余白量を調整する。
 */
const RecommendedArticles: React.FC<{
  articleId: string;
  labels: LabelResult[];
  /** 本文の ArticleNav に既に表示されている記事の articleId（重複除外用） */
  excludeArticleIds?: string[];
  /** 関連する記事から溢れた記事の articleId（先頭に固定表示。記法の並び順を保持） */
  pinnedArticleIds?: string[];
  /** 実際に表示するおすすめ記事の件数を親へ通知する（末尾余白の調整用） */
  onCountChange?: (count: number) => void;
}> = ({ articleId, labels, excludeArticleIds, pinnedArticleIds, onCountChange }) => {
  const [loading, setLoading] = React.useState(true);
  const [recommended, setRecommended] = React.useState<ArticleSummary[]>([]);
  const [expanded, setExpanded] = React.useState(false);

  const onOpen = useOpenArticle('article');

  const labelIds = React.useMemo(() => labels.map((l) => l.labelId), [labels]);
  const excludeKey = React.useMemo(
    () => (excludeArticleIds ?? []).slice().sort().join(','),
    [excludeArticleIds],
  );
  // pinned は「並び順」に意味があるためソートせずそのまま join して依存にする。
  const pinnedKey = React.useMemo(() => (pinnedArticleIds ?? []).join(','), [pinnedArticleIds]);

  React.useEffect(() => {
    const pinnedIds = (pinnedArticleIds ?? []).filter((id) => id && id !== articleId);
    // 溢れ記事もラベルも無ければおすすめは無い。
    if (labelIds.length === 0 && pinnedIds.length === 0) {
      setRecommended([]);
      setLoading(false);
      return;
    }

    let active = true;
    setLoading(true);
    setExpanded(false);

    // 溢れ記事（先頭固定）はメタを個別取得、ラベル集計おすすめは一覧取得。並行実行する。
    const pinnedPromise =
      pinnedIds.length > 0
        ? Promise.allSettled(pinnedIds.map((id) => getArticle(id)))
        : Promise.resolve([] as PromiseSettledResult<ArticleSummary>[]);
    const listPromise =
      labelIds.length > 0
        ? listArticle({ labelIds, grouping: true }).then((res) => res.articles)
        : Promise.resolve([] as ArticleSummary[]);

    Promise.all([pinnedPromise, listPromise])
      .then(([pinnedResults, listArticles]) => {
        if (!active) return;

        // 1. 溢れ記事（取得できたもののみ・記法順を維持）。
        const pinnedArticles: ArticleSummary[] = [];
        const pinnedShownIds = new Set<string>();
        pinnedResults.forEach((r) => {
          if (r.status === 'fulfilled') {
            const a = r.value;
            if (!pinnedShownIds.has(a.articleId)) {
              pinnedShownIds.add(a.articleId);
              pinnedArticles.push(a);
            }
          }
        });

        // 2. ラベル集計おすすめ（self・ArticleNav 既出・溢れ記事は除外）。
        const selfLabelIds = new Set(labelIds);
        const excludeIds = new Set<string>(excludeArticleIds ?? []);
        pinnedShownIds.forEach((id) => excludeIds.add(id));
        const labelRecommended = scoreAndSort(listArticles, selfLabelIds, articleId, excludeIds);

        // 溢れ記事を先頭に、続けてラベル集計おすすめ。全体で最大 MAX_TOTAL 件。
        const combined = [...pinnedArticles, ...labelRecommended].slice(0, MAX_TOTAL);
        setRecommended(combined);
        setLoading(false);
      })
      .catch((e) => {
        if (!active) return;
        // おすすめ記事は付加的な要素なので、取得失敗時は欄を出さない（記事本体はエラーにしない）。
        setRecommended([]);
        setLoading(false);
        // eslint-disable-next-line no-console
        console.error(e);
      });
    return () => {
      active = false;
    };
    // labelIds / pinned は文字列配列。join で依存を安定させる。
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [articleId, labelIds.join(','), excludeKey, pinnedKey]);

  const visible = expanded ? recommended : recommended.slice(0, DEFAULT_VISIBLE);

  // 表示件数を親へ通知（末尾余白の調整用）。
  React.useEffect(() => {
    onCountChange?.(visible.length);
  }, [visible.length, onCountChange]);

  if (loading) {
    // 取得対象（ラベル or 溢れ記事）があるときだけローディングを出す。
    const hasSource = labelIds.length > 0 || (pinnedArticleIds ?? []).length > 0;
    if (!hasSource) return null;
    return (
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        sx={{ color: 'text.secondary', py: 1 }}
      >
        <CircularProgress size={16} />
      </Stack>
    );
  }

  if (recommended.length === 0) return null;

  const hasMore = recommended.length > DEFAULT_VISIBLE;

  return (
    <Stack spacing={1.5}>
      <RelatedArticles title="おすすめ記事" articles={visible} onOpen={onOpen} />
      {hasMore && !expanded && (
        <Button
          size="small"
          variant="text"
          onClick={() => setExpanded(true)}
          sx={{
            alignSelf: 'flex-start',
            textTransform: 'none',
            fontWeight: 600,
            borderRadius: 2,
            color: 'text.secondary',
            '&:hover': { bgcolor: 'action.hover', color: 'primary.main' },
          }}
        >
          もっと見る（残り{recommended.length - DEFAULT_VISIBLE}件）
        </Button>
      )}
    </Stack>
  );
};

export default RecommendedArticles;
